import { useCallback, useEffect, useMemo } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UserRole } from 'common/contracts';
import { UserDetails } from 'pages/Login/Login.types';

import { ELocalStorageKeys, ELogoutMessage } from '../constants/enums';
import useAxios from '../hooks/useAxios';
import { useLogin } from '../hooks/useLogin';
import { localStorageUtil } from '../utils/localStorageUtil';

import {
  EApiRoutes,
  getApiRouteOrMock
} from './api.utils';
import usePermissions from './usePermissions';


function transformToUserDetails(
  data: any,
  cachedUserDetails?: UserDetails | null
): UserDetails {
  const apiData = data?.result || data;
  
  const hasCompleteData = apiData?.projects !== undefined || apiData?.portals !== undefined;
  
  const fullName = apiData?.name || apiData?.userName || '';
  const nameParts = fullName.split(' ');
  
  const version = apiData?.version || cachedUserDetails?.version;
  const role = version 
    ? UserRole.SUPER_ADMIN 
    : (apiData?.role || cachedUserDetails?.role);
  
  return {
    email: apiData?.email || cachedUserDetails?.email || '',
    firstName: apiData?.firstName || nameParts[0] || cachedUserDetails?.firstName || '',
    lastName: apiData?.lastName || nameParts.slice(1).join(' ') || cachedUserDetails?.lastName || '',
    userName: apiData?.userName || apiData?.name || `${apiData?.firstName || ''} ${apiData?.lastName || ''}`.trim() || cachedUserDetails?.userName,
    userId: apiData?.userId || apiData?._id || cachedUserDetails?.userId || '',
    role,
    projects: hasCompleteData ? apiData?.projects : cachedUserDetails?.projects,
    portals: hasCompleteData ? apiData?.portals : cachedUserDetails?.portals,
    version,
    company: apiData?.company || cachedUserDetails?.company,
    companyDisplayName: apiData?.companyDisplayName || cachedUserDetails?.companyDisplayName,
    isOnboarding: apiData?.isOnboarding !== undefined ? apiData?.isOnboarding : cachedUserDetails?.isOnboarding
  };
}


export function useUserDetails() {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const { cleanUserLoginData } = useLogin();
  const { setPermissions, getPermissions } = usePermissions();
  
  const token = localStorageUtil.getAny(ELocalStorageKeys.JWT_TOKEN);
  const isCookieAuth = typeof token === 'string' && token.startsWith('cookie-auth:');
  
  const isLoggedIn = useMemo(() => {
    return Boolean(token);
  }, [token]); 

  // For MFA users, get stored user details
  const storedUserDetails = isCookieAuth 
    ? localStorageUtil.getAny<UserDetails>(ELocalStorageKeys.USER_DETAILS)
    : null;

  const initialData = useMemo(() => {
    const cachedData = queryClient.getQueryData<UserDetails>([EApiRoutes.USERS]);
    if (cachedData) return cachedData;
    
    if (storedUserDetails) {
      queryClient.setQueryData([EApiRoutes.USERS], storedUserDetails);
      return storedUserDetails;
    }
    
    return null;
  }, [queryClient, storedUserDetails]);

  const getUsers = useQuery({
    queryKey: [EApiRoutes.USERS],
    staleTime: 60 * 60 * 1000,
    refetchInterval: 60 * 60 * 1000, 
    refetchIntervalInBackground: false, 
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    retry: false,
    queryFn: async (): Promise<UserDetails> => {
      const freshToken = localStorageUtil.getAny(ELocalStorageKeys.JWT_TOKEN);
      const tokenTTL = localStorageUtil.getAny(ELocalStorageKeys.JWT_TOKEN_TTL);
      
      if (!freshToken) {
        throw new Error('No token available');
      }
      
      const isCookieAuthToken = typeof freshToken === 'string' && freshToken.startsWith('cookie-auth:');
      if (!isCookieAuthToken && tokenTTL && tokenTTL - Date.now() <= 0) {
        cleanUserLoginData(ELogoutMessage.SESSION_EXPIRED);
        throw new Error('Token expired');
      }
      
      try {
        const loginResponse = await axios.post(
          getApiRouteOrMock(EApiRoutes.LOGIN_USER_TOKEN),
          { accessToken: freshToken },
          { withCredentials: true }
        );
        
        const loginData = loginResponse?.data || loginResponse;
        return transformToUserDetails(loginData);
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 500) {
          cleanUserLoginData(ELogoutMessage.SESSION_EXPIRED);
        }
        throw error;
      }
    },  
    enabled: isLoggedIn && !storedUserDetails
  });

  const userDetails = useMemo(() => {
    if (getUsers.data) {
      return getUsers.data as UserDetails;
    }
 
    return initialData ?? null;
  }, [getUsers.data, initialData]);

  useEffect(() => {
    if (userDetails?.projects) {
      const currentPermissions = getPermissions();
      if (!currentPermissions) {
        setPermissions();
      }
    }
  }, [userDetails?.projects, getPermissions, setPermissions]);

  const permissions = getPermissions();
  
  const isLoading = isLoggedIn && (
    (getUsers.isLoading && !userDetails) ||
    (!!userDetails?.projects && !permissions)
  );

  const refetchUserDetails = useCallback(async () => {
    try {
      const data = await axios.get(getApiRouteOrMock(EApiRoutes.USERS));
      const currentUserDetails = queryClient.getQueryData<UserDetails>([EApiRoutes.USERS]);
      const freshUserDetails = transformToUserDetails(data, currentUserDetails);
      
      queryClient.setQueryData([EApiRoutes.USERS], freshUserDetails);
      
      if (isCookieAuth) {
        localStorageUtil.set(ELocalStorageKeys.USER_DETAILS, freshUserDetails);
      }
      
      return freshUserDetails;
    } catch (error) {
      console.error('Failed to refetch user details:', error);
      throw error;
    }
  }, [axios, queryClient, isCookieAuth]);

  return {
    userDetails,
    isLoading,
    isError: getUsers.isError,
    error: getUsers.error,
    refetch: getUsers.refetch,
    refetchUserDetails
  };
}

